// The four authored landmarks — hours of empty plain is a treadmill, so the
// walk gets destinations. Set dressing only (no gameplay, no collision);
// names are placeholders pending founder naming. Each sits on the ground at
// its heightAt and only draws when the player is near enough to matter.

import { heightAt, landmarkById } from "@bebop/shared";
import * as THREE from "three";
import { cloneAuthoredAsset, disposeAuthoredObject, getAuthoredTemplate } from "./authored-assets";
import { castsShadows, Tracker } from "./common";
import { cloth, dress, surface as forged, triplanar } from "./forge";
import { condenserTower } from "./kit";

/** POIs draw inside this range (fog swallows them long before). */
const VISIBLE_DIST = 2600;

interface PoiEntry {
  x: number;
  z: number;
  group: THREE.Group;
}

/**
 * A landmark's material, dressed from the forge.
 *
 * Landmarks are the only mid-field SUBJECTS the wilds vistas have — the flats
 * frame is a barge, the mesa frame is a gate — so a flat albedo on one of
 * them is the whole shot. Each takes the forged surface its story wants, at a
 * tile sized to the object rather than to the sheet.
 */
function material(
  t: Tracker,
  name: string,
  color: number,
  meters: number,
  emissive = 0,
  intensity = 0,
  orm = false,
): THREE.Material {
  return dress(
    t.mat(
      new THREE.MeshStandardMaterial({
        roughness: 1,
        ...(orm ? { metalness: 1 } : {}),
        ...(emissive ? { emissive, emissiveIntensity: intensity } : {}),
      }),
    ),
    name,
    color,
    { orm, meters },
  );
}

/** ~5 km NE: a broken hull arc, half-buried. Embers still glow. */
function buildWreck(t: Tracker): THREE.Group {
  if (getAuthoredTemplate("frontier-wreck")) {
    const authored = cloneAuthoredAsset("frontier-wreck");
    const light = new THREE.PointLight(0xff6a3a, 30, 26, 1.9);
    light.position.set(2, 2, 2);
    authored.add(light);
    return authored;
  }
  const g = new THREE.Group();
  const hull = material(t, "iron", 0x2a2028, 1, 0, 0, true);
  const rib = new THREE.Mesh(t.geo(new THREE.TorusGeometry(9, 1.1, 6, 14, 2.2)), hull);
  rib.rotation.set(Math.PI / 2.3, 0.4, 0.2);
  rib.position.y = 4;
  g.add(rib);
  const rib2 = new THREE.Mesh(t.geo(new THREE.TorusGeometry(7, 0.9, 6, 12, 1.8)), hull);
  rib2.rotation.set(Math.PI / 2.6, -0.7, 0.9);
  rib2.position.set(9, 2.5, 5);
  g.add(rib2);
  const plate = new THREE.Mesh(t.geo(new THREE.BoxGeometry(7, 0.8, 4)), hull);
  plate.rotation.set(0.2, 0.6, 0.35);
  plate.position.set(-6, 0.6, 4);
  g.add(plate);
  const ember = new THREE.PointLight(0xff6a3a, 30, 26, 1.8);
  ember.position.set(2, 2, 2);
  g.add(ember);
  return g;
}

/** ~3 km SW: a field of hissing vents. */
function buildGeyserField(t: Tracker): THREE.Group {
  const g = new THREE.Group();
  const stone = material(t, "stone", 0x27313a, 2, 0x35e0c8, 0.12);
  const placements = [
    [0, 0, 2.6],
    [7, 3, 1.8],
    [-6, 5, 2.2],
    [4, -6, 1.5],
    [-5, -4, 3.1],
    [11, -2, 1.3],
    [-11, 1, 1.7],
  ] as const;
  for (const [px, pz, s] of placements) {
    if (getAuthoredTemplate("frontier-geyser")) {
      const vent = cloneAuthoredAsset("frontier-geyser");
      vent.position.set(px, 0, pz);
      vent.scale.setScalar(s);
      vent.rotation.y = px * 0.7;
      g.add(vent);
      continue;
    }
    const cone = new THREE.Mesh(t.geo(new THREE.ConeGeometry(s, s * 1.4, 7)), stone);
    cone.position.set(px, s * 0.7, pz);
    g.add(cone);
  }
  const glow = new THREE.PointLight(0x35e0c8, 24, 30, 1.9);
  glow.position.set(0, 3, 0);
  g.add(glow);
  return g;
}

/** ~7 km E: something older than the settlement. */
function buildObelisk(t: Tracker): THREE.Group {
  if (getAuthoredTemplate("frontier-obelisk")) {
    const authored = cloneAuthoredAsset("frontier-obelisk");
    const light = new THREE.PointLight(0x8f5bd6, 36, 40, 1.9);
    light.position.set(0, 15, 0);
    authored.add(light);
    return authored;
  }
  const g = new THREE.Group();
  const shaft = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(3, 26, 3)),
    material(t, "stone", 0x141420, 2),
  );
  shaft.position.y = 13;
  shaft.rotation.y = Math.PI / 5;
  g.add(shaft);
  const strip = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(0.4, 24, 0.4)),
    t.mat(
      new THREE.MeshStandardMaterial({
        color: 0x8f5bd6,
        roughness: 0.85,
        emissive: 0x8f5bd6,
        emissiveIntensity: 1.4,
      }),
    ),
  );
  strip.position.set(1.6, 12.5, 1.6);
  strip.rotation.y = Math.PI / 5;
  g.add(strip);
  const aura = new THREE.PointLight(0x8f5bd6, 36, 40, 1.9);
  aura.position.y = 15;
  g.add(aura);
  return g;
}

/** In the SE flats: a freighter that never lifted again — the flats
 *  vista's staged hero subject (t035's grounded-ship role).
 *
 *  Round 4 ("no exterior vista has a sun-keyed subject with real pixel
 *  weight"): the barge moves from ~400 m to ~220 m down the frozen
 *  sightline, turns BROADSIDE (r3's yaw pointed the hull down the
 *  sightline — foreshortened to "an unreadable speck cluster"), and
 *  grows to 1.7×. Since the camera looks into the sun quadrant, the
 *  visible side is the bounce side — pale hull + the doubled warm
 *  hemisphere carry it (t035 sells exactly this). Two brass engine
 *  cowls give the frame its §e hero-metal probe read, and a blob AO
 *  ellipse anchors the hull to the ground (the §f downgrade table's
 *  blob-shadows-under-ships line — it parks beyond the shadow box). */
function buildDerelictBarge(t: Tracker): THREE.Group {
  if (getAuthoredTemplate("frontier-barge")) {
    const group = new THREE.Group();
    const authored = cloneAuthoredAsset("frontier-barge");
    authored.rotation.y = -0.9;
    authored.scale.setScalar(1.7);
    group.add(authored);
    return group;
  }
  const g = new THREE.Group();
  const inner = new THREE.Group();
  inner.rotation.y = -0.9;
  inner.scale.setScalar(1.7);
  g.add(inner);
  // Sun-bleached pale (t2 measured the visible broadside at Y111 against
  // a Y194 sky — solid but still heavy; t035's hull is nearly sand-toned,
  // its shade side carried entirely by albedo × bounce).
  // Round 6: the hull carries the streaked/grimed plaster map — the r5
  // critics read the single flat albedo as "a cardboard cutout with no
  // weathering language"; gravity streaks + mottle are the §e weathering
  // grammar at zero extra draws. (The wave-6 bounce card is what lifts
  // this shade side off its Y46 crush.)
  // WAVE 11: the hull carries forged PLATING — panel seams, a rivet row
  // inside every seam, paint failing to bare metal at the fasteners, and rust
  // weeping down from them. Through r6 it wore the settlement's plaster
  // streak map on a UV repeat, which is why the r5 critics read the largest
  // object in the flats frame as "a cardboard cutout with no weathering
  // language". The tile is 4 m against a 34 m hull, so the panelling reads as
  // ship-scale construction rather than as texture.
  const hull = material(t, "hull", 0xc4b394, 4, 0, 0, true);
  const dark = material(t, "hull", 0x83735c, 4, 0, 0, true);
  const body = new THREE.Mesh(t.geo(new THREE.BoxGeometry(34, 7, 9)), hull);
  body.position.y = 2.6;
  body.rotation.z = 0.06;
  inner.add(body);
  const bow = new THREE.Mesh(t.geo(new THREE.CylinderGeometry(4.5, 4.5, 9, 3, 1)), hull);
  bow.rotation.set(Math.PI / 2, 0, Math.PI / 2);
  bow.position.set(19, 3.4, 0);
  inner.add(bow);
  const castle = new THREE.Mesh(t.geo(new THREE.BoxGeometry(6, 6, 7)), dark);
  castle.position.set(-11, 8, 0);
  castle.rotation.z = 0.05;
  inner.add(castle);
  const mast = new THREE.Mesh(t.geo(new THREE.BoxGeometry(0.9, 13, 0.9)), dark);
  mast.position.set(-9.5, 13, 1);
  mast.rotation.z = -0.18;
  inner.add(mast);
  const spine = new THREE.Mesh(t.geo(new THREE.BoxGeometry(26, 0.8, 1.4)), dark);
  spine.position.set(2, 6.4, -2.5);
  spine.rotation.z = 0.06;
  inner.add(spine);
  // Stern engine cowls: weathered brass, §e hero-metal recipe — the probe
  // (unclipped HDR sun) reads on them from any angle, shadow side or not.
  // Roughness 0.35, not mirror (t2: at 0.14 the camera-side cowl mirrored
  // the dark ground behind the lens and read as a black disc — a rougher
  // lobe averages the bright sky/sun mips and glows warm from any angle).
  const cowlMat = triplanar(
    t.mat(
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        metalness: 1,
        roughness: 1,
        // The 9 here was compensation for a probe with no sun in it: the
        // cowls "read as flat discs" because the only thing to reflect was a
        // gradient. The disc is real now and the brass class returns it at
        // x6 (render/forge.ts), so this goes back to the default.
        envMapIntensity: 1,
      }),
    ),
    forged("brass"),
    { orm: true, meters: 1, normalScale: 0.7 },
  );
  const cowlGeo = t.geo(new THREE.CylinderGeometry(2.1, 2.4, 4.2, 12));
  for (const cz of [-2.4, 2.4]) {
    const cowl = new THREE.Mesh(cowlGeo, cowlMat);
    cowl.rotation.z = Math.PI / 2;
    cowl.position.set(-18.6, 3.6, cz);
    inner.add(cowl);
  }
  // Wave 10 r2: the 21×6.5 m soft ellipse that used to fake "grounded
  // mass" is GONE. It was the single clearest instance of the r1 verdict
  // — the largest object in the flats frame wearing a decal instead of a
  // shadow — and the barge now sits inside the ±130 m shadow box and
  // casts a real one across the playa.
  return g;
}

/** ~6 km N: a gate through the mesa country. */
function buildMesaGate(t: Tracker): THREE.Group {
  if (getAuthoredTemplate("frontier-mesa-gate")) {
    const authored = cloneAuthoredAsset("frontier-mesa-gate");
    const light = new THREE.PointLight(0xe8913a, 26, 34, 1.9);
    light.position.set(0, 16, 0);
    authored.add(light);
    return authored;
  }
  const g = new THREE.Group();
  const rust = material(t, "iron", 0x3a2a1c, 2, 0, 0, true);
  for (const side of [-10, 10]) {
    const pillar = new THREE.Mesh(t.geo(new THREE.BoxGeometry(4, 18, 4)), rust);
    pillar.position.set(side, 9, 0);
    g.add(pillar);
  }
  const lintel = new THREE.Mesh(t.geo(new THREE.BoxGeometry(27, 3, 4.5)), rust);
  lintel.position.y = 19;
  g.add(lintel);
  const lamp = new THREE.PointLight(0xe8913a, 26, 34, 1.9);
  lamp.position.y = 16;
  g.add(lamp);
  return g;
}

/** Placements come from the shared landmark table (north is −z) — the
 *  server scatters derelict equipment around the same coordinates, and a
 *  landmark drawn anywhere but where the world thinks it is would strand
 *  whoever walked to it. */
const POI_BUILDERS: ReadonlyArray<{
  id: string;
  build: (t: Tracker) => THREE.Group;
}> = [
  { id: "wreck", build: buildWreck },
  { id: "geyser-field", build: buildGeyserField },
  { id: "obelisk", build: buildObelisk },
  { id: "mesa-gate", build: buildMesaGate },
  // Round 4 moved the barge for hero framing (~180 m from the frozen
  // flats camera); its coordinates live in the shared landmark table.
  { id: "derelict-barge", build: buildDerelictBarge },
];

/**
 * THE WAYPOST — a midground subject for the ridgeland vista.
 *
 * The flats frame has its grounded barge and the mesa frame has its gate, and
 * both are the reason those two shots read as places. The ridgeland crest had
 * nothing: rock fields running unbroken to the fog line, which is a texture,
 * not a composition. A condenser mast is exactly what the frontier would put
 * out there — the settlement runs one, these pull water out of the air for
 * whoever is walking — so the same builder serves, and the silhouette does the
 * work a landmark has to do at a hundred metres: a vertical against the haze
 * with sky through its bracing.
 *
 * THE FEN DOES NOT GET ONE, and that is a measurement, not an oversight. The
 * "edge" vista is the tightest frame in the whole set (63.6 fps with nothing
 * added), and a mast in it measured 60.0 at 90 m, 60.0 at 135 m and 60.7 at
 * 170 m — the cost is the object existing in that scene at all, not its screen
 * area, so no distance buys it back. The fen already has its leaning snags for
 * verticals. The fps gate outranks a composition preference.
 *
 * These are RENDER-LOCAL placements, not shared landmarks: they carry no
 * gameplay, the server scatters nothing around them, and nothing walks to
 * them. Coordinates are a hundred-odd metres down the frozen vista's
 * sightline, which is where the reference puts its subject.
 */
const WAYPOSTS: ReadonlyArray<{ x: number; z: number; yaw: number }> = [
  // Ridgeland, 120 m down the crest vista's sightline and 25 m off its axis.
  //
  // The first placement put it at 265 m, which a terrain probe said was clear
  // of the intervening ridge by four degrees — and it rendered as a six-pixel
  // gold speck, because a LATTICE has no mass: at that range every member is
  // under a pixel wide and the aerial perspective erases it, leaving only the
  // condenser catching the sun. A filigree subject has to be close enough to
  // resolve its filigree. At 120 m it spans nine degrees of a seventy-degree
  // lens and the bracing reads.
  { x: -366, z: -2586, yaw: 0.9 },
];

export class PoiLayer {
  /** Parent under the floating-origin root. */
  readonly group = new THREE.Group();
  private tracker = new Tracker();
  private entries: PoiEntry[] = [];

  constructor() {
    const wayTimber = material(this.tracker, "wood", 0x6f6047, 1);
    const wayIron = material(this.tracker, "iron", 0x3f3830, 0.5, 0, 0, true);
    const wayBrass = triplanar(
      this.tracker.mat(
        new THREE.MeshStandardMaterial({
          color: 0xffffff,
          metalness: 1,
          roughness: 1,
          envMapIntensity: 1,
        }),
      ),
      forged("brass"),
      { orm: true, meters: 1, normalScale: 0.7 },
    );
    const wayCloth = this.tracker.mat(
      cloth("canvas", 0x8a7a5e, { normalScale: 0, meters: 0.5 }, { side: THREE.DoubleSide }),
    );
    for (const post of WAYPOSTS) {
      const g = condenserTower(this.tracker, {
        timber: wayTimber,
        iron: wayIron,
        brass: wayBrass,
        cloth: wayCloth,
      });
      castsShadows(g);
      g.position.set(post.x, heightAt(post.x, post.z), post.z);
      g.rotation.y = post.yaw;
      g.visible = false;
      this.group.add(g);
      this.entries.push({ x: post.x, z: post.z, group: g });
    }
    for (const def of POI_BUILDERS) {
      const at = landmarkById(def.id);
      if (!at) continue;
      const g = def.build(this.tracker);
      // Wave 10 r2 — the landmarks join the sun. Not one POI mesh was a
      // shadow caster in r1, which is why the flats' hero barge floated.
      castsShadows(g);
      g.position.set(at.x, heightAt(at.x, at.z), at.z);
      g.visible = false;
      this.group.add(g);
      this.entries.push({ x: at.x, z: at.z, group: g });
    }
  }

  update(px: number, pz: number): void {
    for (const entry of this.entries) {
      const dist = Math.hypot(entry.x - px, entry.z - pz);
      entry.group.visible = dist < VISIBLE_DIST;
    }
  }

  dispose(): void {
    disposeAuthoredObject(this.group);
    this.tracker.dispose();
  }
}
